import { permissions } from '@repo/permissions'
import { z } from '@repo/schema'
import { LoadStatusAliasSchema } from '@repo/schema/load'
import { TableConfigurationJsonSchema } from '@repo/schema/table-configuration'
import { tableSortParser } from '@repo/table/query-parsers'
import type { TableConfigurationDefinition } from '@repo/table/table-configuration'

import {
  isStatusGroupTab,
  resolveLoadsTab,
  TAB_STATUS_GROUPS,
} from '../../(table)/_utils/loads-tabs'
import {
  DEFAULT_LOADS_TAB,
  LOADS_PAGE_SIZE,
  loadsPerPageParser,
} from '../../(table)/_utils/loads-query-params'
import { loadsFiltersParser, loadsFiltersSchema } from '../../(table)/_utils/money-total-filters'

const ALL_STATUSES_TAB = 'all'

const SortingSchema = z.array(z.object({ desc: z.boolean(), id: z.string() }))
const FiltersSchema = z.array(TableConfigurationJsonSchema)

/** `status: null` stores the All tab: every status. */
const StoredLoadsQuerySchema = z.object({
  filters: FiltersSchema.catch([]),
  pageSize: z.number().int().min(1).catch(LOADS_PAGE_SIZE),
  search: z.string().catch(''),
  sorting: SortingSchema.catch([]),
  status: z
    .string()
    .nullable()
    .catch(DEFAULT_LOADS_TAB)
    .transform(status => (status === null ? null : resolveLoadsTab(status))),
  tableModes: z
    .object({ infiniteScroll: z.boolean().catch(false) })
    .catch({ infiniteScroll: false }),
})

export type LoadsTableQuery = z.output<typeof StoredLoadsQuerySchema>

const defaultQuery: LoadsTableQuery = {
  filters: [],
  pageSize: LOADS_PAGE_SIZE,
  search: '',
  sorting: [],
  status: DEFAULT_LOADS_TAB,
  tableModes: { infiniteScroll: false },
}

const StatusFilterSchema = z.object({
  columnId: z.literal('status'),
  operator: z.string(),
  values: z.array(z.string()),
})

const MULTIPLE_STATUS_OPERATORS = { exclude: 'exclude if any of', include: 'include any of' }

const statusFilter = (operator: 'exclude' | 'include', values: string[]) => ({
  columnId: 'status',
  operator: values.length > 1 ? MULTIPLE_STATUS_OPERATORS[operator] : operator,
  type: 'multiOption',
  values,
})

/**
 * A Saved View hides the status tabs, so its tab becomes the Status filter it stands for,
 * combined with any Status filter the user chose. Active is stored as the statuses it leaves
 * out, like the tab, so a status added later still shows. When the two share no status, the
 * user's filter wins: a view that can never show a load is never what was meant.
 */
export const withTabAsStatusFilter = (query: LoadsTableQuery): LoadsTableQuery => {
  if (query.status === null) return query
  // A Cancel reason filter pins the request to cancelled loads, which no tab shows.
  if (query.filters.some(filter => filter.columnId === 'cancel_reason')) {
    return { ...query, status: null }
  }

  const tab = query.status
  const tabStatuses: readonly string[] = isStatusGroupTab(tab) ? TAB_STATUS_GROUPS[tab] : []
  const chosen = query.filters
    .map(filter => StatusFilterSchema.safeParse(filter).data)
    .find(filter => filter !== undefined)
  const excluding = chosen?.operator.startsWith('exclude') ?? false
  const allowed = LoadStatusAliasSchema.options.filter(
    status =>
      tabStatuses.includes(status) && (!chosen || chosen.values.includes(status) !== excluding),
  )
  if (allowed.length === 0) return { ...query, status: null }

  const others = query.filters.filter(filter => !StatusFilterSchema.safeParse(filter).success)
  const filter =
    tab === 'active' && (!chosen || excluding)
      ? statusFilter(
          'exclude',
          LoadStatusAliasSchema.options.filter(status => !allowed.includes(status)),
        )
      : statusFilter('include', allowed)

  return { ...query, filters: [...others, filter], status: null }
}

const readStatus = (raw: null | string) => {
  const tab = resolveLoadsTab(raw ?? undefined)
  return tab === ALL_STATUSES_TAB ? null : tab
}

// A stored filter comes back with its keys reordered by the database, unlike the one in the URL.
const withSortedKeys = (filter: LoadsTableQuery['filters'][number]) =>
  Object.fromEntries(Object.entries(filter).toSorted(([a], [b]) => a.localeCompare(b)))

const canonical = (query: LoadsTableQuery) =>
  JSON.stringify([
    query.filters.map(withSortedKeys),
    query.pageSize,
    query.search,
    query.sorting,
    query.status,
    query.tableModes.infiniteScroll,
  ])

const setOrDelete = (search: URLSearchParams, key: string, value: null | string) => {
  if (value === null) search.delete(key)
  else search.set(key, value)
}

export const loadsTableConfiguration: TableConfigurationDefinition<LoadsTableQuery> = {
  defaultQuery,
  label: 'Loads',
  pathname: '/loads',
  permission: permissions.loads.list,
  query: {
    // Schema version 1 is the only version; older versions would migrate here.
    // Views saved before CK-2965, and those the CK-2354 backfill migrated, store a tab.
    decode: (stored, schemaVersion) => {
      if (schemaVersion !== 1) return null
      const query = StoredLoadsQuerySchema.parse(stored)
      // Older filter values upgrade as they do from the URL, so the view opens unchanged.
      const filters = FiltersSchema.parse(loadsFiltersSchema.parse(query.filters))
      return withTabAsStatusFilter({ ...query, filters })
    },
    // Saving from Default Loads, the live query still carries its tab.
    encode: withTabAsStatusFilter,
    equals: (a, b) => canonical(a) === canonical(b),
    read: search => {
      const rawFilters = search.get('filters')
      const rawSort = search.get('sort')

      return {
        filters: FiltersSchema.catch([]).parse(
          rawFilters === null ? [] : (loadsFiltersParser.parse(rawFilters) ?? []),
        ),
        pageSize: loadsPerPageParser.parse(search.get('perPage') ?? '') ?? LOADS_PAGE_SIZE,
        search: search.get('search') ?? '',
        sorting: SortingSchema.catch([]).parse(
          rawSort === null ? [] : (tableSortParser.parse(rawSort) ?? []),
        ),
        status: readStatus(search.get('status')),
        tableModes: { infiniteScroll: search.get('infiniteScroll') === 'true' },
      }
    },
    write: (current, query) => {
      const search = new URLSearchParams(current)
      search.delete('page')
      setOrDelete(
        search,
        'filters',
        // The same JSON nuqs writes for `loadsFiltersParser`.
        query.filters.length ? JSON.stringify(query.filters) : null,
      )
      setOrDelete(search, 'infiniteScroll', query.tableModes.infiniteScroll ? 'true' : null)
      setOrDelete(
        search,
        'perPage',
        query.pageSize === LOADS_PAGE_SIZE ? null : String(query.pageSize),
      )
      setOrDelete(search, 'search', query.search || null)
      setOrDelete(
        search,
        'sort',
        query.sorting.length ? tableSortParser.serialize(query.sorting) : null,
      )
      setOrDelete(
        search,
        'status',
        query.status === DEFAULT_LOADS_TAB ? null : (query.status ?? ALL_STATUSES_TAB),
      )
      return search
    },
  },
  schemaVersion: 1,
  tableKey: 'loads',
}
